import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMcsContext } from "../MCS.context";
import { AuthContext } from "../../../../../../context/authContext";
import { lightTable } from "../../../../../../styles/muiProps";
import { buildSchedule4DColumns, getTotalCost } from "./columns";
import { getMcsStatusForTasks } from "./api";
import { buildSiColumn } from "./SiTableCell";
import {
  getScheduleTasks,
  enrichTasksWithMcsStatus,
  createScheduleTask,
  updateScheduleTask,
  buildProcurementProgressMapForContext,
  buildProcurementCostMapForContext,
  applyEstimationBudgetToScheduleRows,
} from "./scheduleTaskApi";
import { useSchedule4D } from "./Schedule4DContext";
import { aggregateSiStatus, patchTaskSiInList } from "./siUtils";
import { useSiToggle } from "./useSiToggle";

/** Parse taskCode "2.1.1-group1" into { code: "2.1.1", groupName: "group1" } */
const parseTaskCode = (taskCode) => {
  if (!taskCode || typeof taskCode !== "string")
    return { code: "", groupName: "" };
  const s = String(taskCode).trim();
  const dashIdx = s.indexOf("-");
  if (dashIdx < 0) return { code: s, groupName: "" };
  return {
    code: s.slice(0, dashIdx).trim(),
    groupName: s.slice(dashIdx + 1).trim(),
  };
};

/** First two segments of code, e.g. "2.1.1" → "2.1" */
const getCodePrefix = (code) => {
  if (!code || typeof code !== "string") return null;
  const parts = String(code).trim().split(".").filter(Boolean);
  if (parts.length < 2) return parts[0] || null;
  return parts.slice(0, 2).join(".");
};

/** True when WBS code is a subtask (e.g. 2.2.1), not a main group row (2.2). */
const isSubtaskWbsTask = (task) => {
  const { code } = parseTaskCode(task?.taskCode || "");
  const segments = String(code).trim().split(".").filter(Boolean);
  return segments.length > 2;
};

const convertDateString = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return `${d.getDate()}-${d.toLocaleString("en-US", { month: "short" })}-${d.getFullYear()}`;
};

// ─── Grouped columns for the main summary table ────────────────────────────
const groupedColumns = [
  {
    accessorKey: "taskCode",
    header: "WBS ID",
    enableEditing: false,
    size: 100,
  },
  {
    accessorKey: "task",
    header: "Group",
    enableEditing: false,
    size: 250,
  },
  {
    accessorKey: "baselineStart",
    header: "Baseline Start",
    enableEditing: false,
    size: 120,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const v = cell.getValue();
      return v ? <div style={{ whiteSpace: "wrap" }}>{convertDateString(v)}</div> : "";
    },
  },
  {
    accessorKey: "baselineEnd",
    header: "Baseline End",
    enableEditing: false,
    size: 120,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const v = cell.getValue();
      return v ? <div style={{ whiteSpace: "wrap" }}>{convertDateString(v)}</div> : "";
    },
  },
  {
    accessorKey: "revisedStart",
    header: "Revised Start",
    enableEditing: false,
    size: 120,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const v = cell.getValue();
      return v ? <div style={{ whiteSpace: "wrap" }}>{convertDateString(v)}</div> : "";
    },
  },
  {
    accessorKey: "revisedEnd",
    header: "Revised End",
    enableEditing: false,
    size: 120,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const v = cell.getValue();
      return v ? <div style={{ whiteSpace: "wrap" }}>{convertDateString(v)}</div> : "";
    },
  },
  {
    id: "siStatus",
    ...buildSiColumn({ readOnly: true }),
  },
  {
    id: "budgetedCost",
    header: "Budgeted Cost ($)",
    enableEditing: false,
    size: 130,
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyCellProps: { align: "center" },
    Cell: ({ row }) => {
      const v = row.original._totalBudgeted;
      return v > 0 ? (
        `$${v.toFixed(2)}`
      ) : (
        <span style={{ color: "gray" }}>-</span>
      );
    },
  },
  {
    id: "cost",
    header: "Cost ($)",
    enableEditing: false,
    size: 120,
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyCellProps: { align: "center" },
    Cell: ({ row }) => {
      const v = row.original._totalCost;
      return v > 0 ? (
        `$${v.toFixed(2)}`
      ) : (
        <span style={{ color: "gray" }}>-</span>
      );
    },
  },
];

// ─── Sub-task modal (individual tasks within a group) ───────────────────────
function Schedule4DSubTaskModal({
  open,
  onClose,
  groupName,
  materials,
  accessToken,
  loggedInUserName,
  plantId,
  onRefresh,
  onSiUpdated,
}) {
  const [saveError, setSaveError] = useState(null);

  const { handleSiChange, siUpdatingIds } = useSiToggle({
    accessToken,
    onUpdated: (taskId, si) => {
      onSiUpdated?.(taskId, si);
    },
    onError: (err) => {
      console.error("Error updating task SI:", err);
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update SI.",
      );
    },
  });

  const columns = useMemo(
    () => buildSchedule4DColumns({ onSiChange: handleSiChange, siUpdatingIds }),
    [handleSiChange, siUpdatingIds],
  );

  const filteredMaterials = useMemo(() => {
    if (!groupName) return [];
    return (materials || []).filter((m) => {
      const { code, groupName: gn } = parseTaskCode(m.taskCode || "");
      if (gn !== groupName) return false;
      const segments = code.split(".").filter(Boolean);
      return segments.length > 2;
    });
  }, [groupName, materials]);

  const table = useMaterialReactTable({
    data: filteredMaterials,
    columns,
    initialState: {
      sorting: [{ id: "taskCode", desc: false }],
    },
    ...lightTable,
    enableStickyHeader: true,
    enableEditing: true,
    editDisplayMode: "row",
    enableRowActions: true,
    positionActionsColumn: "last",
    muiTableContainerProps: { sx: { maxHeight: "500px" } },
    onEditingRowSave: async ({ values, table: tbl, row }) => {
      setSaveError(null);
      const description =
        values.description ??
        row.original.task ??
        row.original.description ??
        (row.original.taggedMaterial
          ? `${row.original.taggedMaterial?.component ?? ""} ${row.original.taggedMaterial?.size ?? ""}`.trim()
          : "");
      const baselineStart =
        values.baselineStart ??
        row.original.baselineStart ??
        row.original.startDate ??
        "";
      const baselineEnd =
        values.baselineEnd ??
        row.original.baselineEnd ??
        row.original.endDate ??
        "";
      const revisedStart =
        values.revisedStart ?? row.original.revisedStart ?? "";
      const revisedEnd = values.revisedEnd ?? row.original.revisedEnd ?? "";
      const rawBudgetedCost =
        values.budgetedCost ??
        row.original.budgetedCost ??
        row.original.estimatedTime ??
        "";
      const budgetedCost =
        rawBudgetedCost === "" || rawBudgetedCost == null
          ? null
          : Number(rawBudgetedCost);

      try {
        if (row.original.scheduleTaskId) {
          await updateScheduleTask({
            accessToken,
            taskId: row.original.scheduleTaskId,
            task: description || row.original.task || "Material",
            taskCode: row.original.taskCode,
            baselineStart: baselineStart || null,
            baselineEnd: baselineEnd || null,
            revisedStart: revisedStart || null,
            revisedEnd: revisedEnd || null,
            budgetedCost,
          });
        } else {
          await createScheduleTask({
            plantId,
            accessToken,
            userName: loggedInUserName,
            description: description || "Material",
            baselineStart: baselineStart || null,
            baselineEnd: baselineEnd || null,
            revisedStart: revisedStart || null,
            revisedEnd: revisedEnd || null,
            budgetedCost,
          });
        }
        if (onRefresh) await onRefresh();
      } catch (err) {
        console.error("Error saving schedule dates to task:", err);
        setSaveError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to save dates.",
        );
      }
      tbl.setEditingRow(null);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Tasks in group: {groupName}</DialogTitle>
      <DialogContent>
        {saveError && (
          <Box
            sx={{
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              bgcolor: "error.light",
              color: "error.contrastText",
            }}
          >
            {saveError}
          </Box>
        )}
        {filteredMaterials.length > 0 ? (
          <Box pt={1}>
            <MaterialReactTable table={table} />
          </Box>
        ) : (
          <Box p={2}>
            <Typography>No tasks in this group.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Schedule4D component ──────────────────────────────────────────────
export default function Schedule4D() {
  const { plant } = useMcsContext();
  const { user, accessToken } = useContext(AuthContext);
  const { setSchedule, setScheduleLoading } = useSchedule4D();
  const loggedInUserName = user?.name ?? user?.email ?? "";
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTaskModal, setSubTaskModal] = useState({
    open: false,
    groupName: null,
  });

  const handleSiUpdated = useCallback((taskId, si) => {
    setMaterials((prev) => patchTaskSiInList(prev, taskId, si));
  }, []);

  useEffect(() => {
    const fetchMaterialsAndTasks = async () => {
      if (!plant?.id) {
        console.log("[Schedule4D] No plant.id, skipping fetch");
        return;
      }
      console.log("plantsssss", plant);
      

      console.log("[Schedule4D] Fetching materials and tasks:", {
        plantId: plant.id,
        hasAccessToken: !!accessToken,
      });

      try {
        setLoading(true);
        setScheduleLoading(true);
        const [scheduleTasks, mcsStatus] = await Promise.all([
          getScheduleTasks({ plantId: plant.id, accessToken }).catch((err) => {
            console.error("[Schedule4D] getScheduleTasks failed:", err);
            return [];
          }),
          getMcsStatusForTasks(plant.id).catch(() => ({
            byTaskCode: new Map(),
            byTaskId: new Map(),
          })),
        ]);
        // console.log("schedule tasks", scheduleTasks);
        console.log("mcs status", mcsStatus);
        let merged = enrichTasksWithMcsStatus(scheduleTasks ?? [], mcsStatus);
        merged = applyEstimationBudgetToScheduleRows(merged, plant?.estData);
        setMaterials(merged);
        const {
          map: procurementProgressMap,
          avgProgress: procurementCalculatedProgress,
        } = buildProcurementProgressMapForContext(merged);
        const {
          budgetedCostMap: procurementBudgetedCostMap,
          actualCostMap: procurementActualCostMap,
        } = buildProcurementCostMapForContext(merged);
        setSchedule(merged, plant.id, {
          procurementProgressMap,
          procurementCalculatedProgress,
          procurementBudgetedCostMap,
          procurementActualCostMap,
        });
      } catch (error) {
        console.error(
          "[Schedule4D] Error fetching Schedule4D materials:",
          error,
        );
        setMaterials([]);
      } finally {
        setLoading(false);
        setScheduleLoading(false);
      }
    };

    fetchMaterialsAndTasks();
  }, [plant?.id, plant?.estData, accessToken]);

  const refetchMaterialsAndTasks = async (savedOverlay) => {
    if (!plant?.id) return;
    try {
      const [scheduleTasks, mcsStatus] = await Promise.all([
        getScheduleTasks({ plantId: plant.id, accessToken }).catch(() => []),
        getMcsStatusForTasks(plant.id).catch(() => ({
          byTaskCode: new Map(),
          byTaskId: new Map(),
        })),
      ]);
      let merged = enrichTasksWithMcsStatus(scheduleTasks ?? [], mcsStatus);
      merged = applyEstimationBudgetToScheduleRows(merged, plant?.estData);
      if (
        savedOverlay?.scheduleTaskId != null &&
        savedOverlay.budgetedCost !== undefined
      ) {
        merged = merged.map((row) =>
          row.scheduleTaskId === savedOverlay.scheduleTaskId
            ? { ...row, budgetedCost: savedOverlay.budgetedCost }
            : row,
        );
      }
      setMaterials(merged);
      const {
        map: procurementProgressMap,
        avgProgress: procurementCalculatedProgress,
      } = buildProcurementProgressMapForContext(merged);
      const {
        budgetedCostMap: procurementBudgetedCostMap,
        actualCostMap: procurementActualCostMap,
      } = buildProcurementCostMapForContext(merged);
      setSchedule(merged, plant.id, {
        procurementProgressMap,
        procurementCalculatedProgress,
        procurementBudgetedCostMap,
        procurementActualCostMap,
      });
    } catch (error) {
      console.error("Error refetching Schedule4D materials:", error);
    }
  };

  // ── Grouping logic ──────────────────────────────────────────────────────
  const groupNameToPrefix = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => {
      const { code, groupName: gn } = parseTaskCode(m.taskCode || "");
      if (gn && code) {
        const p = getCodePrefix(code);
        if (p && !map.has(gn)) map.set(gn, p);
      }
    });
    return map;
  }, [materials]);

  const groupedRows = useMemo(() => {
    const groupMap = new Map();
    (materials || []).forEach((m) => {
      const { groupName: gn } = parseTaskCode(m.taskCode || "");
      if (!gn) return;
      if (!groupMap.has(gn)) groupMap.set(gn, []);
      groupMap.get(gn).push(m);
    });

    const rows = [];
    groupMap.forEach((tasks, gn) => {
      const prefix = groupNameToPrefix.get(gn) || "";
      const subtasks = tasks.filter(isSubtaskWbsTask);
      const dateSource = subtasks.length > 0 ? subtasks : tasks;

      const siStatus = aggregateSiStatus(subtasks.length > 0 ? subtasks : tasks);

      const toDate = (v) => (v ? String(v).slice(0, 10) : "");
      const starts = dateSource
        .map((t) => toDate(t.baselineStart || t.startDate))
        .filter(Boolean)
        .sort();
      const ends = dateSource
        .map((t) => toDate(t.baselineEnd || t.endDate))
        .filter(Boolean)
        .sort();
      const rStarts = dateSource
        .map((t) => toDate(t.revisedStart))
        .filter(Boolean)
        .sort();
      const rEnds = dateSource
        .map((t) => toDate(t.revisedEnd))
        .filter(Boolean)
        .sort();

      const totalBudgeted = subtasks.reduce((sum, t) => {
        if (t.budgetedCost == null || t.budgetedCost === "") return sum;
        const v = Number(t.budgetedCost);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);

      const totalCost = subtasks.reduce((sum, t) => {
        const c = getTotalCost(t);
        return sum + (c != null && Number.isFinite(c) ? c : 0);
      }, 0);

      rows.push({
        id: `group-${gn}`,
        taskCode: prefix,
        task: gn,
        baselineStart: starts[0] || "",
        baselineEnd: ends[ends.length - 1] || "",
        revisedStart: rStarts[0] || "",
        revisedEnd: rEnds[rEnds.length - 1] || "",
        _siStatus: siStatus,
        _totalBudgeted: totalBudgeted,
        _totalCost: totalCost,
        _isGroup: true,
        _groupName: gn,
      });
    });

    rows.sort((a, b) => {
      const dateA = a.baselineStart ? new Date(a.baselineStart) : new Date(0);
      const dateB = b.baselineStart ? new Date(b.baselineStart) : new Date(0);
      return dateA - dateB;
    });

    return rows;
  }, [materials, groupNameToPrefix]);

  // ── Main grouped table ──────────────────────────────────────────────────
  const table = useMaterialReactTable({
    data: groupedRows,
    columns: groupedColumns,
    ...lightTable,
    enableStickyHeader: true,
    enableEditing: false,
    muiTableContainerProps: { sx: { maxHeight: "600px" } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (row.original._isGroup) {
          setSubTaskModal({
            open: true,
            groupName: row.original._groupName,
          });
        }
      },
      sx: { cursor: "pointer" },
    }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        Loading materials...
      </Box>
    );
  }

  return (
    <Box>
      <MaterialReactTable table={table} />
      {subTaskModal.open && (
        <Schedule4DSubTaskModal
          open={subTaskModal.open}
          onClose={() => setSubTaskModal({ open: false, groupName: null })}
          groupName={subTaskModal.groupName}
          materials={materials}
          accessToken={accessToken}
          loggedInUserName={loggedInUserName}
          plantId={plant?.id}
          onRefresh={refetchMaterialsAndTasks}
          onSiUpdated={handleSiUpdated}
        />
      )}
    </Box>
  );
}
